// Theme clair/sombre. L'etat initial est pose sur <html data-theme> par
// public/theme-init.js avant le premier rendu ; ce module lit cet etat,
// le bascule et memorise le choix. Meme cle que theme-init.js.
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'jarvis-theme';

export function parseTheme(value: unknown): Theme | null {
  return value === 'light' || value === 'dark' ? value : null;
}

export function readStoredTheme(storage: Pick<Storage, 'getItem'> | undefined): Theme | null {
  try {
    return parseTheme(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Choix enregistré pour le compte : un thème, ou suivre l'appareil. */
export type ThemePref = 'system' | Theme;

export function parseThemePref(value: unknown): ThemePref | null {
  return value === 'system' ? 'system' : parseTheme(value);
}

/** Événement émis sur window quand le thème change (bouton d'en-tête, Profil, serveur). */
export const THEME_EVENT = 'jarvis:theme';

export function storeTheme(storage: Pick<Storage, 'setItem'> | undefined, theme: Theme): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Stockage indisponible (navigation privee, site bloque) : le choix vaut pour la page.
  }
}

export function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function currentTheme(): Theme {
  return parseTheme(document.documentElement.getAttribute('data-theme')) ?? systemTheme();
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    window.dispatchEvent(new Event(THEME_EVENT));
  } catch {
    // Environnement sans window (tests) : rien à prévenir.
  }
}

export function nextTheme(theme: Theme): Theme {
  return theme === 'dark' ? 'light' : 'dark';
}

function safeLocalStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** Bascule le theme courant, l'applique et le memorise. Renvoie le nouveau theme. */
export function toggleTheme(): Theme {
  const theme = nextTheme(currentTheme());
  applyTheme(theme);
  storeTheme(safeLocalStorage(), theme);
  return theme;
}

/** Thème choisi localement (copie de la préférence du compte), ou null. */
export function storedTheme(): Theme | null {
  return readStoredTheme(safeLocalStorage());
}

/**
 * Applique une préférence de thème venue du compte (serveur) ou du Profil, et
 * met à jour la copie locale (anti-flash) : « system » efface la copie et suit
 * l'appareil.
 */
export function applyThemePref(pref: ThemePref): void {
  if (pref === 'system') {
    try {
      safeLocalStorage()?.removeItem(THEME_STORAGE_KEY);
    } catch {
      // Stockage indisponible : sans effet.
    }
    applyTheme(systemTheme());
    return;
  }
  applyTheme(pref);
  storeTheme(safeLocalStorage(), pref);
}
